import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Compass, Megaphone, FileText, Users, Loader2, ExternalLink, Trash2, History as HistoryIcon, CheckCircle2, AlertCircle, FileDown, Hash, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_META = {
  "comment-goldmine": { label: "Comment Goldmine", icon: MessageSquare, route: "/comment-goldmine", color: "text-emerald-400" },
  "instagram-trending": { label: "Virale Posts (Trends)", icon: Compass, route: "/trends?tab=ig-trending", color: "text-violet-400" },
  "facebook-ads-spy": { label: "Facebook Ads Spy", icon: Megaphone, route: "/facebook-tools?tab=ads", color: "text-cyan-400" },
  "facebook-page-audit": { label: "Facebook Page Audit", icon: FileText, route: "/facebook-tools?tab=page", color: "text-cyan-400" },
  "facebook-group-listener": { label: "Facebook Group Listener", icon: Users, route: "/facebook-tools?tab=group", color: "text-cyan-400" },
  "hashtag-check": { label: "Hashtag-Check", icon: Hash, route: "/hashtag-check", color: "text-cyan-400" },
  "carousel": { label: "Karussell", icon: Layers, route: "/carousel", color: "text-cyan-400" },
};

function StatusPill({ status }) {
  if (status === "completed") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-2.5 h-2.5" />fertig</span>;
  }
  if (status === "running") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"><Loader2 className="w-2.5 h-2.5 animate-spin" />läuft</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-red-500/10 text-red-300 border border-red-500/30"><AlertCircle className="w-2.5 h-2.5" />fehlgeschlagen</span>;
}

function formatTimeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 86400 * 7) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function AnalysisHistoryPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tools/history?limit=100`);
      setRuns(data.runs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "History konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/tools/history/${id}`);
      setRuns(rs => rs.filter(r => r.id !== id));
      toast.success("Eintrag gelöscht");
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const reopen = (run) => {
    const meta = TOOL_META[run.tool];
    if (!meta) { toast.error("Tool nicht erkannt"); return; }
    // Pass jobId in URL so the page can re-open the result
    const sep = meta.route.includes("?") ? "&" : "?";
    navigate(`${meta.route}${sep}jobId=${run.id}`);
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="analysis-history-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <HistoryIcon className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Meine Analysen</h1>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        )}

        {!loading && runs?.length === 0 && (
          <div className="text-center py-16" data-testid="history-empty">
            <HistoryIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-400 font-medium mb-1">Noch keine Analysen</p>
            <p className="text-xs text-zinc-600 max-w-sm mx-auto leading-relaxed">
              Sobald du eine Comment-Goldmine, FB-Page oder Trends-Analyse startest, taucht sie hier auf —
              auch wenn du den Tab schließt.
            </p>
          </div>
        )}

        {!loading && runs?.length > 0 && (
          <div className="space-y-2" data-testid="history-list">
            {runs.map(r => {
              const meta = TOOL_META[r.tool] || { label: r.tool, icon: HistoryIcon, color: "text-zinc-400" };
              const Icon = meta.icon;
              return (
                <div
                  key={r.id}
                  className="group p-3 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-cyan-500/30 transition-colors flex items-center gap-3"
                  data-testid={`history-row-${r.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-zinc-800/80 flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white truncate">{meta.label}</p>
                      <StatusPill status={r.status} />
                    </div>
                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                      {r.input_label || "—"} · {formatTimeAgo(r.created_at)}
                    </p>
                    {r.error && r.status === "failed" && (
                      <p className="text-[10px] text-red-300/70 mt-0.5 truncate" title={r.error}>{r.error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {r.tool === "comment-goldmine" && r.status === "completed" && (
                      <a
                        href={`${API}/tools/jobs/${r.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 rounded-md text-zinc-400 hover:text-cyan-300 hover:bg-zinc-800 transition-colors"
                        title="Als PDF herunterladen"
                        data-testid={`history-pdf-${r.id}`}
                      >
                        <FileDown className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => reopen(r)}
                      className="p-2 rounded-md text-zinc-400 hover:text-cyan-300 hover:bg-zinc-800 transition-colors"
                      title="Ergebnis öffnen"
                      data-testid={`history-open-${r.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-zinc-800 transition-colors"
                      title="Eintrag löschen"
                      data-testid={`history-delete-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
